/**
 * Telefones de Emergência — nativo do app (0.1.56). Mobile-first.
 *
 * Mesma tela para o MORADOR (só vê e liga) e para o SÍNDICO/Dono (adiciona, edita,
 * apaga) — o backend informa `pode_editar`. Substitui o antigo PDF avulso da
 * Biblioteca; o Portal e a gestão leem a MESMA lista.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Ambulance, ArrowLeft, Building2, Check, DoorOpen, Droplet, Flame, Loader2,
  MoveVertical, Pencil, Phone, PhoneCall, Plus, Shield, ShieldAlert, Trash2, User, X, Zap,
  type LucideIcon,
} from "lucide-react";
import { api, ApiError } from "../lib/api";

interface Telefone { id: number; nome: string; telefone: string; tipo: string; obs: string }
interface Resp { itens: Telefone[]; tipos: string[]; pode_editar: boolean }

// Cada tipo → ícone + rótulo + cor. 'outros' é o padrão.
const TIPOS: Record<string, { icone: LucideIcon; rotulo: string; cor: string }> = {
  bombeiros: { icone: Flame, rotulo: "Bombeiros", cor: "from-red-600 to-red-800" },
  policia: { icone: ShieldAlert, rotulo: "Polícia", cor: "from-slate-700 to-slate-900" },
  samu: { icone: Ambulance, rotulo: "SAMU / Ambulância", cor: "from-rose-600 to-rose-800" },
  portaria: { icone: DoorOpen, rotulo: "Portaria", cor: "from-sky-600 to-sky-800" },
  sindico: { icone: User, rotulo: "Síndico", cor: "from-brand-600 to-brand-800" },
  gas: { icone: Flame, rotulo: "Gás", cor: "from-orange-600 to-orange-800" },
  energia: { icone: Zap, rotulo: "Energia", cor: "from-amber-500 to-amber-700" },
  agua: { icone: Droplet, rotulo: "Água", cor: "from-cyan-600 to-cyan-800" },
  elevador: { icone: MoveVertical, rotulo: "Elevador", cor: "from-indigo-600 to-indigo-800" },
  seguranca: { icone: Shield, rotulo: "Segurança", cor: "from-emerald-600 to-emerald-800" },
  administradora: { icone: Building2, rotulo: "Administradora", cor: "from-teal-600 to-teal-800" },
  outros: { icone: Phone, rotulo: "Outros", cor: "from-slate-500 to-slate-700" },
};
const tipoInfo = (t: string) => TIPOS[t] ?? TIPOS.outros;

// Só dígitos para o link "tel:" (mantém o texto exibido como foi digitado).
const soDigitos = (s: string) => (s || "").replace(/[^\d+]/g, "");

export function TelefonesEmergenciaPage({ condominioId, onVoltar, somenteLeitura }: { condominioId: number; onVoltar: () => void; somenteLeitura?: boolean }) {
  const qc = useQueryClient();
  const chave = ["telefones-emergencia", condominioId];
  const inval = () => qc.invalidateQueries({ queryKey: chave });

  const { data, isLoading } = useQuery<Resp>({
    queryKey: chave,
    queryFn: () => api.get<Resp>(`/condominios/${condominioId}/telefones-emergencia`),
  });

  // No Portal é só leitura (mesmo para o síndico); a gestão é pelo painel do condomínio.
  const podeEditar = !somenteLeitura && !!data?.pode_editar;
  const itens = data?.itens ?? [];
  const [novo, setNovo] = useState(false);

  return (
    <div className="max-w-lg mx-auto px-1">
      <div className="flex items-center justify-between pt-2 pb-4">
        <button onClick={onVoltar} title="Voltar"
          className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border text-slate-600 bg-white border-slate-200 hover:bg-slate-50">
          <ArrowLeft size={16} /> Voltar
        </button>
        <div className="flex items-center gap-2 text-rose-700 font-semibold">
          <PhoneCall size={18} /> Telefones de Emergência
        </div>
      </div>

      {podeEditar && !novo && (
        <button onClick={() => setNovo(true)}
          className="mb-3 inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-brand-700 text-white hover:bg-brand-800">
          <Plus size={16} /> Adicionar telefone
        </button>
      )}
      {podeEditar && novo && (
        <Editor condominioId={condominioId} tipos={data?.tipos ?? []} onPronto={() => { setNovo(false); inval(); }}
          onCancelar={() => setNovo(false)} />
      )}

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 text-slate-500 py-16">
          <Loader2 size={18} className="animate-spin" /> Carregando…
        </div>
      ) : itens.length === 0 ? (
        <div className="mt-2 p-5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          {podeEditar
            ? "Nenhum telefone cadastrado ainda. Use “Adicionar telefone” para criar a lista — ela aparece automaticamente no Portal do Condômino."
            : "Ainda não há telefones de emergência cadastrados. O síndico pode adicioná-los."}
        </div>
      ) : (
        <div className="space-y-2.5">
          {itens.map((t) => (
            <LinhaTelefone key={t.id} t={t} condominioId={condominioId} podeEditar={podeEditar}
              tipos={data?.tipos ?? []} inval={inval} />
          ))}
        </div>
      )}
    </div>
  );
}

function LinhaTelefone({ t, condominioId, podeEditar, tipos, inval }: {
  t: Telefone; condominioId: number; podeEditar: boolean; tipos: string[]; inval: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const info = tipoInfo(t.tipo);
  const Icone = info.icone;

  const remover = useMutation({
    mutationFn: () => api.delete(`/condominios/${condominioId}/telefones-emergencia/${t.id}`),
    onSuccess: inval,
  });

  if (editando) {
    return (
      <Editor condominioId={condominioId} tipos={tipos} item={t}
        onPronto={() => { setEditando(false); inval(); }} onCancelar={() => setEditando(false)} />
    );
  }

  return (
    <div className={`rounded-2xl p-4 text-white shadow-sm bg-gradient-to-br ${info.cor}`}>
      <div className="flex items-center gap-3">
        <a href={`tel:${soDigitos(t.telefone)}`}
          className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0 active:scale-95"
          title="Ligar">
          <Icone size={26} />
        </a>
        <a href={`tel:${soDigitos(t.telefone)}`} className="min-w-0 flex-1">
          <div className="text-lg font-bold truncate">{t.nome}</div>
          <div className="text-sm text-white/90">{t.telefone}</div>
          {t.obs && <div className="text-xs text-white/75 truncate">{t.obs}</div>}
        </a>
        {podeEditar && (
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            <button onClick={() => setEditando(true)} title="Editar"
              className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center"><Pencil size={15} /></button>
            <button onClick={() => { if (confirm(`Apagar “${t.nome}”?`)) remover.mutate(); }} title="Apagar"
              className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center"><Trash2 size={15} /></button>
          </div>
        )}
      </div>
    </div>
  );
}

function Editor({ condominioId, tipos, item, onPronto, onCancelar }: {
  condominioId: number; tipos: string[]; item?: Telefone; onPronto: () => void; onCancelar: () => void;
}) {
  const [nome, setNome] = useState(item?.nome ?? "");
  const [telefone, setTelefone] = useState(item?.telefone ?? "");
  const [tipo, setTipo] = useState(item?.tipo ?? "outros");
  const [obs, setObs] = useState(item?.obs ?? "");
  const [erro, setErro] = useState<string | null>(null);
  const inp = "w-full text-sm rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500";

  const salvar = useMutation({
    mutationFn: () => {
      const corpo = { nome, telefone, tipo, obs };
      return item
        ? api.put(`/condominios/${condominioId}/telefones-emergencia/${item.id}`, corpo)
        : api.post(`/condominios/${condominioId}/telefones-emergencia`, corpo);
    },
    onSuccess: onPronto,
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível salvar."),
  });

  const listaTipos = tipos.length ? tipos : Object.keys(TIPOS);

  return (
    <div className="mb-3 rounded-xl border border-slate-200 bg-white p-3 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input className={inp} placeholder="Nome (ex.: Bombeiros)" value={nome} onChange={(e) => setNome(e.target.value)} />
        <input className={inp} placeholder="Telefone (ex.: 193)" value={telefone} onChange={(e) => setTelefone(e.target.value)} />
        <select className={`${inp} bg-white`} value={tipo} onChange={(e) => setTipo(e.target.value)}>
          {listaTipos.map((k) => <option key={k} value={k}>{tipoInfo(k).rotulo}</option>)}
        </select>
        <input className={inp} placeholder="Observação (opcional)" value={obs} onChange={(e) => setObs(e.target.value)} />
      </div>
      {erro && <p className="text-xs text-rose-600">{erro}</p>}
      <div className="flex items-center gap-2">
        <button disabled={!nome.trim() || !telefone.trim() || salvar.isPending} onClick={() => salvar.mutate()}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-brand-700 text-white disabled:opacity-50">
          {salvar.isPending ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Salvar
        </button>
        <button onClick={onCancelar} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-slate-300 text-slate-600">
          <X size={15} /> Cancelar
        </button>
      </div>
    </div>
  );
}
